/**
 * Treść strony /warsztaty-florystyczne — wydzielona z pages/warsztaty-florystyczne.tsx
 * jako osobny, JSON-serializable config. Logika formularza zapytania
 * (walidacja, wysyłka, stan `WorkshopInquiry`) zostaje w kodzie — tu
 * wydzielona jest treść redakcyjna, w tym etykiety/opcje/placeholdery
 * pól formularza (ten sam poziom szczegółowości co kontaktContent.ts).
 */
export interface WorkshopTypeCard {
  iconKey: string;
  label: string;
  desc: string;
  aspect: string;
}

export interface IncludeCard {
  iconKey: string;
  title: string;
  desc: string;
}

export interface ProcessStep {
  n: string;
  title: string;
  note: string;
}

export interface FaqEntry {
  q: string;
  a: string;
}

export interface GalleryImage {
  aspect: string;
  label: string;
  gridColumn: string;
  gridRow?: string;
  fillHeight?: boolean;
}

export interface QuickLink {
  label: string;
  href: string;
}

export interface SelectOption {
  value: string;
  label: string;
}

export const WARSZTATY_CONTENT = {
  hero: {
    kicker: 'Warsztaty florystyczne · Lublin',
    tagline: 'Stwórz coś pięknego własnymi rękami.',
    headingLine1: 'Warsztaty florystyczne',
    headingLine2: 'w Lublinie',
    body: 'Ułóż własną kompozycję i poznaj podstawy pracy z kwiatami. Wybierz spotkanie indywidualne lub zapytaj o warsztaty dla grupy - temat, termin i warunki ustalimy wspólnie.',
    ctaAskLabel: 'Zapytaj o warsztaty',
    ctaSeeLabel: 'Co stworzymy?',
  },
  notFlorist: {
    eyebrow: 'Dla każdego',
    headingLine1: 'Nie musisz',
    headingLine2: 'być florystą',
    body1: 'Warsztaty są dla każdego - niezależnie od tego, czy pierwszy raz trzymasz sekator florystyczny, czy masz już doświadczenie w pracy z kwiatami.',
    body2: 'Pokażemy Ci, jak dobierać kwiaty, kolory i dodatki, jak budować kompozycję oraz jak nadać jej własny charakter.',
    imagePlaceholder: 'Zdjęcie - praca z kwiatami',
  },
  format: {
    eyebrow: 'Format',
    heading: 'Nauka indywidualna czy wspólne tworzenie w grupie?',
    individual: {
      tag: 'Indywidualnie',
      title: 'Warsztaty 1:1',
      body: 'Spotkanie 1:1 wybierz, gdy masz konkretny temat albo chcesz pracować we własnym tempie. W zapytaniu napisz, czy zaczynasz od podstaw, czy masz już doświadczenie. Możesz wskazać technikę lub kompozycję, którą chcesz poznać, a zakres omówimy przed ustaleniem spotkania.',
      bullets: ['Osoby początkujące', 'Osoby chcące rozwijać umiejętności', 'Kreatywny czas dla siebie', 'Przygotowanie do konkretnego projektu'] as string[],
      ctaLabel: 'Zapytaj o warsztaty indywidualne',
    },
    group: {
      tag: 'Grupowo',
      title: 'Warsztaty dla grup',
      body: 'Warsztaty dla grupy mogą być częścią urodzin, wieczoru panieńskiego lub spotkania zespołu. Podaj przewidywaną liczbę uczestników i okazję. Przy wspólnym wyborze tematu weźcie pod uwagę doświadczenie grupy oraz to, co każdy chciałby zabrać do domu.',
      bullets: ['Przyjaciele', 'Rodziny', 'Urodziny', 'Wieczory panieńskie', 'Integracje firmowe', 'Zamknięte grupy', 'Wydarzenia specjalne'] as string[],
      ctaLabel: 'Zorganizuj warsztaty dla grupy',
    },
  },
  whatWeCreate: {
    eyebrow: 'Tematy',
    heading: 'Bukiet, flower box czy wianek - wybierz temat',
    types: [
      { iconKey: 'flower2', label: 'Bukiet sezonowy', desc: 'Naturalny bukiet z kwiatów dostępnych w danym sezonie.', aspect: '3/4' },
      { iconKey: 'package', label: 'Flower box', desc: 'Elegancka kompozycja w ozdobnym pudełku.', aspect: '4/3' },
      { iconKey: 'circle', label: 'Kompozycja w naczyniu', desc: 'Dekoracyjna kompozycja kwiatowa w ceramicznym naczyniu.', aspect: '3/4' },
      { iconKey: 'leaf', label: 'Wianek', desc: 'Wianek dopasowany do sezonu lub wyjątkowej okazji.', aspect: '4/3' },
      { iconKey: 'star', label: 'Kompozycje okolicznościowe', desc: 'Dekoracje florystyczne na konkretne wydarzenie lub okazję.', aspect: '3/4' },
      { iconKey: 'scissors', label: 'Według własnego pomysłu', desc: 'Masz inny pomysł? Ustalmy temat warsztatów indywidualnie.', aspect: '4/3' },
    ] satisfies WorkshopTypeCard[],
    noteText: 'To tylko przykłady. Jeśli masz własny pomysł, opowiedz nam o nim - możemy wspólnie ustalić temat warsztatów.',
    noteLinkLabel: 'Napisz do nas →',
  },
  custom: {
    eyebrow: 'Indywidualnie',
    headingLine1: 'Masz własny',
    headingAccent: 'pomysł?',
    body: 'Opisz kompozycję, którą chcesz wykonać, lub podeślij inspirację w rozmowie z kwiaciarnią. Ustalimy, jaki zakres będzie odpowiedni dla Ciebie albo grupy. Przed potwierdzeniem warsztatów warto wyjaśnić przewidywany czas spotkania, cenę, miejsce i materiały. Samo wysłanie formularza jest zapytaniem o możliwość organizacji.',
    ctaLabel: 'Opowiedz nam o swoim pomyśle',
    quickLinks: ['Wieczór panieński z kwiatami', 'Integracja zespołu', 'Urodzinowe warsztaty florystyczne', 'Kreatywne spotkanie z przyjaciółkami', 'Warsztaty dla firmy', 'Warsztaty indywidualne'] as string[],
  },
  includes: {
    eyebrow: 'W zestawie',
    heading: 'Materiały i praktyka podczas warsztatów',
    items: [
      { iconKey: 'flower2', title: 'Kwiaty i materiały', desc: 'Wszystko, czego potrzebujesz do stworzenia swojej kompozycji.' },
      { iconKey: 'scissors', title: 'Narzędzia', desc: 'Nie musisz przychodzić z własnym sprzętem florystycznym.' },
      { iconKey: 'leaf', title: 'Instruktaż', desc: 'Prowadzący pokaże Ci kolejne etapy tworzenia kompozycji.' },
      { iconKey: 'package', title: 'Własna kompozycja', desc: 'To, co stworzysz podczas warsztatów, zabierasz ze sobą.' },
      { iconKey: 'star', title: 'Atmosfera', desc: 'Stawiamy na przyjemny, swobodny i kreatywny czas.' },
      { iconKey: 'checkCircle2', title: 'Wiedza', desc: 'Praktyczne zasady pracy z kwiatami, które możesz wykorzystać również później.' },
    ] satisfies IncludeCard[],
  },
  forWhom: {
    eyebrow: 'Uczestnicy',
    heading: 'Dla kogo są warsztaty?',
    items: ['Dla Ciebie', 'Dla przyjaciół', 'Dla par', 'Dla rodzin', 'Na wieczór panieński', 'Na urodziny', 'Dla firm', 'Na wyjątkową okazję'] as string[],
    ctaLabel: 'Zapytaj →',
    footerNote: 'Nie musisz mieć żadnego doświadczenia. Wystarczy, że lubisz kwiaty i masz ochotę stworzyć coś własnego.',
  },
  process: {
    eyebrow: 'Proces',
    heading: 'Jak wyglądają warsztaty?',
    steps: [
      { n: '01', title: 'Wybieramy temat', note: 'Opowiedz nam, czego szukasz i co chcesz stworzyć - możemy też zaproponować temat.' },
      { n: '02', title: 'Ustalamy termin i liczbę uczestników', note: 'Dopasowujemy datę i warunki do Twoich potrzeb i możliwości.' },
      { n: '03', title: 'Przygotowujemy kwiaty i materiały', note: 'Wszystko, czego potrzebujecie, będzie gotowe na czas.' },
      { n: '04', title: 'Spotykamy się i tworzymy', note: 'Czas na pracę z kwiatami - w dobrej atmosferze i bez pośpiechu.' },
      { n: '05', title: 'Zabierasz swoją kompozycję do domu', note: 'To, co stworzysz, jest Twoje.' },
    ] satisfies ProcessStep[],
  },
  gallery: {
    eyebrow: 'Galeria',
    heading: 'Tak wygląda czas z kwiatami',
    images: [
      { aspect: '4/3', label: 'Zdjęcie - atmosfera warsztatów', gridColumn: 'span 7', gridRow: 'span 2', fillHeight: true },
      { aspect: '4/3', label: 'Zdjęcie - detal dłoni', gridColumn: 'span 5' },
      { aspect: '4/3', label: 'Zdjęcie - gotowa kompozycja', gridColumn: 'span 5' },
      { aspect: '3/4', label: 'Zdjęcie - kwiaty', gridColumn: 'span 4' },
      { aspect: '3/4', label: 'Zdjęcie - praca', gridColumn: 'span 4' },
      { aspect: '3/4', label: 'Zdjęcie - detale', gridColumn: 'span 4' },
    ] satisfies GalleryImage[],
  },
  b2b: {
    eyebrow: 'B2B',
    headingLine1: 'Warsztaty florystyczne',
    headingLine2: 'dla firm',
    imagePlaceholder: 'Zdjęcie - integracja firmowa',
    body: 'Szukasz pomysłu na integrację zespołu, spotkanie firmowe albo nietypowe wydarzenie dla pracowników? Warsztaty florystyczne mogą być spokojną i kreatywną alternatywą dla klasycznych spotkań integracyjnych.',
    ctaAskLabel: 'Zapytaj o warsztaty dla firmy',
    ctaOfferLabel: 'Oferta dla firm',
  },
  faq: {
    eyebrow: 'FAQ',
    heading: 'Termin, cena i organizacja warsztatów',
    items: [
      { q: 'Czy warsztaty są dla początkujących?', a: 'Tak. Warsztaty są dostępne dla każdego - niezależnie od doświadczenia. Prowadzimy zarówno osoby, które po raz pierwszy trzymają sekator, jak i te, które mają już pewną wiedzę florystyczną.' },
      { q: 'Czy mogę zapisać się indywidualnie?', a: 'Tak. Oferujemy warsztaty indywidualne 1:1 - skontaktuj się z nami, opisz, czego chcesz się nauczyć, i wspólnie ustalimy termin.' },
      { q: 'Czy można zorganizować warsztaty dla grupy?', a: 'Tak. Organizujemy warsztaty dla małych i większych grup - dla znajomych, rodzin, współpracowników. Napisz nam, ile osób będzie uczestniczyć, a przygotujemy propozycję.' },
      { q: 'Ile osób może wziąć udział?', a: 'Liczba uczestników zależy od rodzaju warsztatów i dostępności miejsca. Skontaktuj się z nami, podając szacunkową liczbę osób - ustalimy szczegóły.' },
      { q: 'Czy można zorganizować warsztaty dla firmy?', a: 'Tak. Warsztaty florystyczne mogą być oryginalną formą integracji lub kreatywnego spotkania dla pracowników. Skontaktuj się z nami lub odwiedź naszą stronę dla firm.' },
      { q: 'Czy warsztaty mogą być prezentem?', a: 'Tak. Warsztaty florystyczne to wyjątkowy prezent - zarówno dla jednej osoby, jak i dla pary czy grupy przyjaciół. Skontaktuj się z nami, aby omówić szczegóły.' },
      { q: 'Czy można zorganizować wieczór panieński?', a: 'Tak. Warsztaty florystyczne na wieczór panieński to elegancka i twórcza alternatywa. Możemy przygotować warsztaty dopasowane do okazji - opisz nam swój pomysł.' },
      { q: 'Czy sami wybieramy temat warsztatów?', a: 'Tak. Możesz wybrać spośród dostępnych tematów - bukiet, flower box, wianek, kompozycja w naczyniu - lub zaproponować własny temat. Lubimy indywidualne zlecenia.' },
      { q: 'Czy kwiaty i materiały są zapewnione?', a: 'Tak. Wszystkie kwiaty, narzędzia i materiały potrzebne do stworzenia kompozycji są zapewnione. Nie musisz przynosić nic własnego.' },
      { q: 'Czy własną kompozycję można zabrać do domu?', a: 'Tak. To, co stworzysz podczas warsztatów, jest Twoje - zabierasz swoją kompozycję do domu.' },
      { q: 'Ile trwają warsztaty?', a: 'Czas trwania zależy od rodzaju warsztatów i liczby uczestników. Szczegóły ustalamy indywidualnie - skontaktuj się z nami.' },
      { q: 'Ile kosztują warsztaty?', a: 'Cena zależy od rodzaju warsztatów, liczby uczestników i wybranego tematu. Skontaktuj się z nami, podając swoje oczekiwania - przygotujemy propozycję.' },
      { q: 'Gdzie odbywają się warsztaty?', a: 'Warsztaty odbywają się w naszych kwiaciarniach w Lublinie - al. Warszawska 23/1 lub ul. Bursztynowa 17/4. Szczegóły lokalizacji ustalamy przy rezerwacji.' },
      { q: 'Czy można ustalić indywidualny termin?', a: 'Tak. Nie pracujemy wyłącznie na stałym harmonogramie - staramy się dopasować termin do Twoich potrzeb. Skontaktuj się z nami z wyprzedzeniem.' },
      { q: 'Czy można zorganizować warsztaty na specjalną okazję?', a: 'Oczywiście. Możemy przygotować warsztaty dopasowane do konkretnej okazji - urodzin, rocznicy, wieczoru panieńskiego, świąt czy innego ważnego momentu.' },
    ] satisfies FaqEntry[],
    quickLinks: [
      { label: 'Zamówienie specjalne', href: '/zamowienie-specjalne/' },
      { label: 'O nas', href: '/o-nas/' },
      { label: 'Kontakt', href: '/kontakt/' },
    ] satisfies QuickLink[],
  },
  formCta: {
    eyebrow: 'Rezerwacja',
    heading: 'Zapytaj o warsztaty w wybranym terminie',
    body: 'Podaj liczbę osób, preferowaną datę, temat i orientacyjny budżet. Jeśli warsztaty mają być prezentem lub częścią większej uroczystości, napisz o tym w wiadomości. Odpowiemy, aby uzgodnić szczegóły; formularz nie potwierdza rezerwacji terminu.',
  },
  form: {
    successHeading: 'Dziękujemy!',
    successBody: 'Otrzymaliśmy Twoje zapytanie dotyczące warsztatów. Skontaktujemy się z Tobą, aby ustalić szczegóły.',
    backToShopLabel: 'Wróć do sklepu',
    sendAnotherLabel: 'Wyślij kolejne zapytanie',
    workshopKindLabel: 'Rodzaj warsztatów',
    workshopKindOptions: ['Indywidualne', 'Dla grupy', 'Dla firmy', 'Nie wiem — chcę porozmawiać'] as string[],
    participantsLabel: 'Liczba uczestników',
    participantsPlaceholder: 'Np. 6',
    occasionLabel: 'Okazja',
    occasionOptions: ['Bez okazji', 'Urodziny', 'Wieczór panieński', 'Integracja', 'Spotkanie rodzinne', 'Prezent', 'Inna'] as string[],
    topicLabel: 'Preferowany temat',
    topicOptions: ['Bukiet', 'Flower box', 'Kompozycja w naczyniu', 'Wianek', 'Inny', 'Chcę ustalić temat wspólnie'] as string[],
    preferredDateLabel: 'Preferowany termin',
    budgetLabel: 'Orientacyjny budżet',
    budgetPlaceholder: 'Np. 200–400 zł',
    messageLabel: 'Opowiedz nam o swoim pomyśle',
    messagePlaceholder: 'Opowiedz nam o swoim pomyśle - okazja, oczekiwania, pytania…',
    contactSectionLabel: 'Dane kontaktowe',
    fullNameLabel: 'Imię i nazwisko',
    fullNamePlaceholder: 'Anna Kowalska',
    phoneLabel: 'Telefon',
    phonePlaceholder: '600 000 000',
    emailLabel: 'E-mail',
    emailPlaceholder: 'anna@przykład.pl',
    disclaimer: 'To jest zapytanie - nie zobowiązuje do zakupu. Skontaktujemy się, żeby ustalić szczegóły.',
    submitLabel: 'Wyślij zapytanie',
    submitLabelSending: 'Wysyłanie…',
    genericServerErrorMessage: 'Błąd serwera',
    genericSubmitErrorMessage: 'Nie udało się wysłać zapytania. Spróbuj ponownie.',
    selectPlaceholder: 'Wybierz',
  },
};
